/**
 * 最初に開く画面（HOME）を決める（PRODUCT_DEV-1070）
 *
 * - **個人設定 → （マネージャーなら）活動サマリ → 会社の既定 → マイ手帳** の順に決める
 *   （個人設定が会社の既定より強い）
 * - PRODUCT_DEV-1092: 部の数字を見られる人（マネージャー）は `"summary"` で活動サマリ（マネージャー画面）を選べ、
 *   **未設定なら会社の既定より先に活動サマリ**（2026-10-08 ユーザ決定「マネージャーはまずマネージャーTOP」）。
 *   見られなくなった人の `"summary"` は未設定として扱う（会社の既定には summary を入れない）
 * - 個人設定 `"default"` は「自分では決めていない（既定に合わせる）」で、未設定と同じに扱う
 *   （家で戻した・既定と同じ画面を選んだときに入る。`personalChoiceFor`。PRODUCT_DEV-1070 10/08 見直し）
 *
 * アプリを開いたときの振り分け（root-loader）・ロゴ（chrome の homePath）・グロナビの家の印が
 * すべてここを通る。判定を各所で書くと、どれか1つだけ古い HOME を指す。
 */
import {
  HOME_SCREEN_FOLLOW_DEFAULT,
  HomeScreenSchema,
  type HomeScreenPreference,
  type PersonalHomeScreen,
} from "@shared-domain/unizo/contract";
import { DIARY_HOME_LABEL } from "@/hooks/useMyPageLabel";
import { ROUTE_MEETINGS_LIST, ROUTE_MYPAGE, ROUTE_MYPAGE_ADMIN } from "@/utils/const";

/** 活動サマリの画面名（グロナビ・ロゴの読み上げ・HOME の選択肢で共通）。 */
export const SUMMARY_HOME_LABEL = "活動サマリ";

export interface HomeTarget {
  /** どの画面が HOME か。グロナビの家の印を付けるタブの判定に使う。 */
  screen: HomeScreenPreference;
  /** ロゴ・「ホームへ戻る」の行き先。 */
  path: string;
  /** 行き先の画面名（ロゴの読み上げ・戻るボタンの文言）。 */
  label: string;
}

/** 端末の下書きなど、型の保証が無い値を HOME の設定として読む。読めなければ null（未設定）。 */
export const parseHomeScreen = (raw: unknown): HomeScreenPreference | null => {
  const parsed = HomeScreenSchema.safeParse(raw);
  return parsed.success ? parsed.data : null;
};

/**
 * @param tenantDefault 会社の初期表示ページ（PRODUCT_DEV-1070）。マネージャーでない人の未設定にだけ効く。
 * @param canViewSummary 部の数字（活動サマリ）を見られるか（`checkCanViewTeamSummary`）。
 *   アプリを開いた直後の root-loader ではまだ分からないので false を渡す（その場合は一度マイ手帳へ送り、
 *   手帳の画面が `consumeHomeEntry` を見て活動サマリへ送り直す）。
 */
export const resolveHome = (
  personal: PersonalHomeScreen | null | undefined,
  tenantDefault: HomeScreenPreference | null | undefined = null,
  canViewSummary = false,
): HomeTarget => {
  // 「既定に合わせる」と、活動サマリを見られなくなった人の summary は未設定として扱う
  const chosen =
    personal === HOME_SCREEN_FOLLOW_DEFAULT || (personal === "summary" && !canViewSummary)
      ? null
      : personal;
  const homeScreen = chosen ?? (canViewSummary ? "summary" : null) ?? tenantDefault ?? "diary";
  // `/` は開いたときに手帳へ送られうるので、一覧に必ず当たる URL を指す（グロナビの商談一覧タブと同じ）
  if (homeScreen === "meetings") {
    return { screen: "meetings", path: ROUTE_MEETINGS_LIST, label: "記録一覧" };
  }
  if (homeScreen === "summary") {
    return { screen: "summary", path: ROUTE_MYPAGE_ADMIN, label: SUMMARY_HOME_LABEL };
  }
  return { screen: "diary", path: ROUTE_MYPAGE, label: DIARY_HOME_LABEL };
};

/**
 * 本人が画面を選んだときに保存する値（PRODUCT_DEV-1070・10/08 見直し）。
 *
 * 自分で決めていないときの画面（マネージャーなら活動サマリ → 会社の初期表示ページ → マイ手帳）と同じなら
 * `"default"`＝既定に合わせる、違えばその画面。家のマークで戻した人・既定と同じ画面を選んだ人まで
 * 個人設定で固めると、会社が初期表示ページを後で変えても届かない。
 */
export const personalChoiceFor = (
  chosen: HomeScreenPreference,
  tenantDefault: HomeScreenPreference | null | undefined,
  canViewSummary = false,
): PersonalHomeScreen =>
  chosen === resolveHome(null, tenantDefault, canViewSummary).screen
    ? HOME_SCREEN_FOLLOW_DEFAULT
    : chosen;

/**
 * 基本設定の選択肢で、いま選ばれている個人設定（PRODUCT_DEV-1070・10/09）。
 *
 * 未設定・`"default"`・見られなくなった人の `"summary"` はどれも「組織の設定に合わせる」（`"default"`）として見せる
 * （`resolveHome` が未設定として扱うものと同じ）。
 */
export const personalSettingOf = (
  saved: PersonalHomeScreen | null | undefined,
  canViewSummary = false,
): PersonalHomeScreen =>
  !saved || (saved === "summary" && !canViewSummary) ? HOME_SCREEN_FOLLOW_DEFAULT : saved;

/**
 * アプリを開いたときの「HOME へ送る」の印（PRODUCT_DEV-1092）。
 *
 * HOME が活動サマリの人も、root-loader の時点では役割が分からないので一度マイ手帳へ送られる。
 * 手帳の画面はこの印があるときだけ活動サマリへ送り直す。**印が無いとき（ナビの「⇄ 個人へ」・
 * マイ手帳のタブで来たとき）は送らない**（押した先から勝手に戻されないように）。
 * 印はページを読み込むたびに1回だけ立ち、読んだら消える。
 */
let homeEntryPending = false;
export const markHomeEntry = (): void => {
  homeEntryPending = true;
};
export const consumeHomeEntry = (): boolean => {
  const pending = homeEntryPending;
  homeEntryPending = false;
  return pending;
};
